import type {
  CartStatus,
  CustomerChannel,
  FulfilmentStatus,
  InvoiceManagedIn,
  OrderManagedIn,
  PaymentStatus,
  ProductSource,
  ProductStatus,
  SyncStatus,
} from '@/types/commerceV2';
import type { RiderStatus } from './riderStatus';

export interface PillMeta {
  label: string;
  color: string;
  bg: string;
}

export const SOURCE_META: Record<ProductSource, PillMeta> = {
  wenext: { label: 'WeNext', color: '#076A2D', bg: '#E7F7EC' },
  shopify: { label: 'Shopify', color: '#5E8E3E', bg: '#F2F7ED' },
  woocommerce: { label: 'WooCommerce', color: '#7F54B3', bg: '#F5F0FA' },
  meta: { label: 'Meta Catalog', color: '#1877F2', bg: '#EFF6FF' },
  whatsapp_catalog: {
    label: 'Meta Catalog',
    color: '#128C7E',
    bg: '#ECFDF5',
  },
};

export const CHANNEL_META: Record<CustomerChannel, PillMeta> = {
  whatsapp: { label: 'WhatsApp', color: '#128C7E', bg: '#ECFDF5' },
  instagram: { label: 'Instagram', color: '#C13584', bg: '#FDF2F8' },
  facebook: { label: 'Facebook', color: '#1877F2', bg: '#EFF6FF' },
  shopify: { label: 'Shopify Store', color: '#5E8E3E', bg: '#F2F7ED' },
  woocommerce: { label: 'WooCommerce Store', color: '#7F54B3', bg: '#F5F0FA' },
  wenext: { label: 'WeNext', color: '#076A2D', bg: '#E7F7EC' },
  manual: { label: 'Manual order', color: '#475467', bg: '#F2F4F7' },
};

export const SYNC_META: Record<SyncStatus, PillMeta> = {
  synced: { label: 'Synced', color: '#076A2D', bg: '#E7F7EC' },
  syncing: { label: 'Syncing', color: '#B54708', bg: '#FFFAEB' },
  not_synced: { label: 'Not synced', color: '#475467', bg: '#F2F4F7' },
  sync_failed: { label: 'Sync failed', color: '#B42318', bg: '#FEF3F2' },
  partially_synced: {
    label: 'Partially synced',
    color: '#B54708',
    bg: '#FFFAEB',
  },
  update_available: {
    label: 'Update available',
    color: '#175CD3',
    bg: '#EFF8FF',
  },
};

export const PRODUCT_STATUS_META: Record<ProductStatus, PillMeta> = {
  active: { label: 'Active', color: '#076A2D', bg: '#E7F7EC' },
  draft: { label: 'Draft', color: '#475467', bg: '#F2F4F7' },
  archived: { label: 'Archived', color: '#667085', bg: '#F2F4F7' },
  out_of_stock: { label: 'Out of stock', color: '#B42318', bg: '#FEF3F2' },
};

export const PAYMENT_META: Record<PaymentStatus, PillMeta> = {
  paid: { label: 'Paid', color: '#076A2D', bg: '#E7F7EC' },
  pending: { label: 'Pending', color: '#B54708', bg: '#FFFAEB' },
  failed: { label: 'Failed', color: '#B42318', bg: '#FEF3F2' },
  refunded: { label: 'Refunded', color: '#475467', bg: '#F2F4F7' },
  cod: { label: 'COD', color: '#175CD3', bg: '#EFF8FF' },
};

export const FULFILMENT_META: Record<FulfilmentStatus, PillMeta> = {
  unfulfilled: { label: 'Unfulfilled', color: '#B54708', bg: '#FFFAEB' },
  processing: { label: 'Processing', color: '#175CD3', bg: '#EFF8FF' },
  shipped: { label: 'Shipped', color: '#0E7490', bg: '#ECFEFF' },
  delivered: { label: 'Delivered', color: '#076A2D', bg: '#E7F7EC' },
  cancelled: { label: 'Cancelled', color: '#B42318', bg: '#FEF3F2' },
};

export const RIDER_STATUS_META: Record<RiderStatus, PillMeta> = {
  rider_pending: { label: 'Awaiting Rider', color: '#B54708', bg: '#FFFAEB' },
  rider_assigned: { label: 'Rider Assigned', color: '#175CD3', bg: '#EFF8FF' },
  rider_pickup: { label: 'Picked Up', color: '#0E7490', bg: '#ECFEFF' },
};

export const INVOICE_META: Record<InvoiceManagedIn, PillMeta> = {
  wenext: { label: 'Available in WeNext', color: '#076A2D', bg: '#E7F7EC' },
  shopify: { label: 'Managed by Shopify', color: '#5E8E3E', bg: '#F2F7ED' },
  woocommerce: {
    label: 'Managed by WooCommerce',
    color: '#7F54B3',
    bg: '#F5F0FA',
  },
  none: { label: 'Not available', color: '#667085', bg: '#F2F4F7' },
};

export const MANAGED_META: Record<OrderManagedIn, PillMeta> = {
  wenext: { label: 'WeNext', color: '#076A2D', bg: '#E7F7EC' },
  shopify: SOURCE_META.shopify,
  woocommerce: SOURCE_META.woocommerce,
  external: { label: 'External', color: '#475467', bg: '#F2F4F7' },
};

export const CART_STATUS_META: Record<CartStatus, PillMeta> = {
  open: { label: 'Open', color: '#B54708', bg: '#FFFAEB' },
  reminded: { label: 'Reminded', color: '#175CD3', bg: '#EFF8FF' },
  recovered: { label: 'Recovered', color: '#076A2D', bg: '#E7F7EC' },
  superseded: { label: 'Superseded', color: '#475467', bg: '#F2F4F7' },
  expired: { label: 'Expired', color: '#475467', bg: '#F2F4F7' },
};

const WOO_ORDER_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending payment',
  'on-hold': 'On hold',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export function wooOrderStatusLabel(rawStatus: unknown): string {
  if (typeof rawStatus !== 'string') return '—';
  const slug = rawStatus.trim().toLowerCase();
  if (!slug) return '—';
  if (WOO_ORDER_STATUS_LABELS[slug]) return WOO_ORDER_STATUS_LABELS[slug];
  return slug
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ');
}

export const WOO_CART_STATUS_META: Record<string, PillMeta> = {
  pending: { label: 'Pending payment', color: '#B54708', bg: '#FFFAEB' },
  'on-hold': { label: 'On hold', color: '#0E7490', bg: '#ECFEFF' },
  failed: { label: 'Failed', color: '#B42318', bg: '#FEF3F2' },
  cancelled: { label: 'Cancelled', color: '#475467', bg: '#F2F4F7' },
};

const WOO_CART_STATUS_HINTS: Record<string, string> = {
  pending: 'Payment never started. Nothing charged, no stock held.',
  'on-hold':
    'Awaiting payment — usually bank transfer, cheque or COD. Stock is held.',
  failed: 'Payment was declined. The customer may not know.',
  cancelled: 'Cancelled in WooCommerce, by the store or the customer.',
};

export function wooCartStatusPill(
  rawStatus: unknown,
): { meta: PillMeta; hint?: string } | null {
  if (typeof rawStatus !== 'string') return null;
  const slug = rawStatus.trim().toLowerCase();
  if (!slug) return null;
  const known = WOO_CART_STATUS_META[slug];
  if (known) return { meta: known, hint: WOO_CART_STATUS_HINTS[slug] };
  return {
    meta: {
      label: wooOrderStatusLabel(slug),
      color: '#475467',
      bg: '#F2F4F7',
    },
  };
}

export interface CartStatusView {
  id: string;
  meta: PillMeta;
  hint?: string;
}

export function cartStatusView(cart: {
  status: CartStatus;
  catalogSource: string;
  raw?: Record<string, unknown> | null;
}): CartStatusView {
  if (cart.catalogSource === 'woocommerce') {
    const rawStatus = cart.raw?.status;
    const woo = wooCartStatusPill(rawStatus);
    if (woo) {
      return {
        id: String(rawStatus).trim().toLowerCase(),
        meta: woo.meta,
        hint: woo.hint,
      };
    }
  }
  return { id: cart.status, meta: CART_STATUS_META[cart.status] ?? UNKNOWN_META };
}

export const UNKNOWN_META: PillMeta = {
  label: '—',
  color: '#98A2B3',
  bg: '#F9FAFB',
};

export function money(amount: number, currency = 'INR'): string {
  const safe = Number.isFinite(amount) ? amount : 0;
  try {
    return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: safe % 1 === 0 ? 0 : 2,
    }).format(safe);
  } catch {
    return `${currency} ${safe.toLocaleString()}`;
  }
}

export function moneySymbol(currency = 'INR'): string {
  try {
    const parts = new Intl.NumberFormat(
      currency === 'INR' ? 'en-IN' : 'en-US',
      { style: 'currency', currency },
    ).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? currency;
  } catch {
    return currency;
  }
}

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const at = new Date(iso).getTime();
  if (!Number.isFinite(at)) return '—';
  const diff = Date.now() - at;
  if (diff < 0) return 'Just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function exactDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
}
